import { useQuery } from '@tanstack/react-query';

import {
  creditLine,
  type CreditLineDimension,
  type CreditLineExposure,
  type CreditLineLine,
  type CreditLineMovement,
  type PolicyholderCreditLine,
} from '@/shared/api/resources';
import type { Money } from '@/shared/api/types';
import { formatDate, formatDateTime, formatMoney } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';

import { gruposDeModalidade } from '../rotulos';
import { Bloco } from './shared';

/**
 * Limite calculado do tomador e do grupo economico (C-LIM).
 *
 * Mostra, por grupo de credito, o aprovado, o utilizado, o reservado, o disponivel, a
 * validade e a situacao de cada dimensao. Sem a metrica de consumo definida em versao
 * ativa (LIM-Q01 a LIM-Q08), o SeguraCore nao controla o limite: a tela diz isso, e
 * nenhuma dimensao tem disponivel para comparar. O dinheiro vem do servidor em centavos
 * e e mostrado pelo formatador comum, sem conta no navegador.
 */

const rotuloDimensao: Record<string, string> = {
  grupo_de_modalidade: 'Grupo de modalidades',
  limite_global: 'Limite global do tomador',
  grupo_economico: 'Grupo economico',
  grupo_economico_total: 'Grupo economico (total)',
};

const rotuloEvento: Record<string, string> = {
  emissao: 'Emissao',
  cancelamento: 'Cancelamento',
  endosso_aumento: 'Endosso de aumento',
  endosso_reducao: 'Endosso de reducao',
  carga_inicial: 'Carga inicial',
  fim_de_vigencia: 'Fim da vigencia',
};

const rotuloConferencia: Record<string, string> = {
  conferido: 'Conferido',
  excederia_em_sombra: 'Excederia (regra em sombra)',
  nao_conferido: 'Nao conferido',
  liberacao: 'Liberacao',
  carga: 'Carga',
};

const rotuloValidade: Record<string, string> = {
  vigente: 'vigente',
  vencido: 'vencido',
  nao_definida: 'validade nao definida',
  sem_vencimento: 'sem vencimento',
};

export const SEM_METRICA =
  'O SeguraCore nao controla este limite: a metrica de consumo nao esta definida ' +
  'em versao ativa de parametros (LIM-Q01 a LIM-Q08). O utilizado e o disponivel nao ' +
  'sao calculados, e nenhum valor desta aba libera uma emissao.';

export const OCULTO_AO_CORRETOR =
  'Limite e taxa ficam ocultos enquanto houver cartao de credito ou de PEP aberto pela ' +
  'execucao do motor (PM-09).';

function grupo(chave: string | undefined): string {
  if (!chave) return 'Nao classificado';
  return gruposDeModalidade[chave] ?? chave;
}

function dinheiro(valor: Money | null) {
  return valor ? formatMoney(valor) : '-';
}

function Situacao({ dimensao }: { dimensao: CreditLineDimension }) {
  if (dimensao.situation === 'calculavel') {
    return <Badge tone="success">Calculavel</Badge>;
  }
  const rotulo =
    dimensao.effect === 'revisao'
      ? 'Revisao de limite'
      : dimensao.effect === 'suspenso'
        ? 'Suspenso'
        : 'Nao verificavel';
  return <Badge tone="warning">{rotulo}</Badge>;
}

function Motivos({ dimensao }: { dimensao: CreditLineDimension }) {
  if (dimensao.reasons.length === 0) return null;
  return (
    <ul className="mt-1 list-disc pl-4 text-2xs text-ink-500">
      {dimensao.reasons.map((motivo) => (
        <li key={motivo.code}>{motivo.message || motivo.code}</li>
      ))}
    </ul>
  );
}

function Validade({ dimensao }: { dimensao: CreditLineDimension }) {
  const situacao = rotuloValidade[dimensao.validity] ?? '';
  if (dimensao.valid_until) {
    return (
      <span>
        {formatDate(dimensao.valid_until)}
        {situacao ? (
          <span className="ml-1 text-2xs text-ink-500">({situacao})</span>
        ) : null}
      </span>
    );
  }
  return <span className="text-ink-400">{situacao || '-'}</span>;
}

function Utilizado({ dimensao }: { dimensao: CreditLineDimension }) {
  return (
    <span className="inline-flex flex-col items-end">
      <span>{dimensao.used ? formatMoney(dimensao.used) : 'nao calculado'}</span>
      {dimensao.informed_used ? (
        <span className="text-2xs text-ink-500">
          informado: {formatMoney(dimensao.informed_used)}
        </span>
      ) : null}
    </span>
  );
}

/** Tabela das dimensoes de uma linha (ou de um grupo economico). */
export function TabelaDeDimensoes({ dimensoes }: { dimensoes: CreditLineDimension[] }) {
  return (
    <DataTable
      columns={[
        {
          key: 'dimensao',
          header: 'Dimensao',
          render: (d) => (
            <span>
              {rotuloDimensao[d.dimension] ?? d.dimension}
              {d.economic_group_name ? (
                <span className="ml-1 text-2xs text-ink-500">
                  {d.economic_group_name}
                </span>
              ) : null}
              {d.modality_group && d.dimension !== 'grupo_de_modalidade' ? (
                <span className="ml-1 text-2xs text-ink-500">
                  {grupo(d.modality_group)}
                </span>
              ) : null}
            </span>
          ),
        },
        {
          key: 'aprovado',
          header: 'Aprovado',
          align: 'right',
          render: (d) => dinheiro(d.approved),
        },
        {
          key: 'utilizado',
          header: 'Utilizado',
          align: 'right',
          render: (d) => <Utilizado dimensao={d} />,
        },
        {
          key: 'reservado',
          header: 'Reservado',
          align: 'right',
          render: (d) => dinheiro(d.reserved),
        },
        {
          key: 'disponivel',
          header: 'Disponivel',
          align: 'right',
          render: (d) => dinheiro(d.available),
        },
        {
          key: 'validade',
          header: 'Validade',
          render: (d) => <Validade dimensao={d} />,
        },
        {
          key: 'situacao',
          header: 'Situacao',
          render: (d) => (
            <div>
              <Situacao dimensao={d} />
              <Motivos dimensao={d} />
            </div>
          ),
        },
      ]}
      items={dimensoes}
      keyOf={(d) =>
        `${d.dimension}-${d.economic_group_id ?? ''}-${d.modality_group ?? ''}`
      }
    />
  );
}

function linhaDoGrupo(linha: CreditLineLine): CreditLineDimension | undefined {
  return linha.dimensions.find((d) => d.dimension === 'grupo_de_modalidade');
}

/** Resumo da politica da empresa: a metrica e as respostas das perguntas abertas. */
function Politica({ dados }: { dados: PolicyholderCreditLine }) {
  if (!dados.controlled) {
    return (
      <Alert tone="warning" title="Metrica nao definida">
        <p>{SEM_METRICA}</p>
        {dados.missing.length > 0 ? (
          <ul className="mt-2 list-disc pl-4">
            {dados.missing.map((motivo) => (
              <li key={motivo.code}>{motivo.message}</li>
            ))}
          </ul>
        ) : null}
      </Alert>
    );
  }
  return (
    <p className="text-2xs text-ink-500">
      Metrica usada: {dados.metric === 'is_nominal' ? 'IS nominal' : dados.metric}, da
      versao {dados.parameter_version} de parametros. O disponivel e o aprovado menos o
      utilizado
      {dados.policy.reserves_proposals ? ' menos o reservado' : ''}, sem ficar negativo.
      {dados.policy.holder_rule_homologated
        ? ' A regra do limite (LIM-008) esta homologada: a emissao que nao cabe e recusada.'
        : ' A regra do limite (LIM-008) esta em sombra: a emissao e registrada e nao e recusada pelo limite.'}
    </p>
  );
}

/** Exposicao factual do tomador, por grupo de credito. */
export function Exposicao({ exposicao }: { exposicao: CreditLineExposure }) {
  return (
    <Bloco titulo="Exposicao">
      <DataTable
        columns={[
          {
            key: 'grupo',
            header: 'Grupo',
            render: (l) =>
              l.modality_group === '*' ? 'Total' : grupo(l.modality_group),
          },
          {
            key: 'abertas',
            header: 'Garantias em aberto',
            align: 'right',
            render: (l) =>
              `${formatMoney(l.open_guarantees.amount)} (${l.open_guarantees.count})`,
          },
          {
            key: 'fiancas',
            header: 'Das quais fianca bancaria',
            align: 'right',
            render: (l) => formatMoney(l.open_bank_guarantees.amount),
          },
          {
            key: 'vencidas',
            header: 'Vencidas',
            align: 'right',
            render: (l) => formatMoney(l.expired_guarantees.amount),
          },
          {
            key: 'canceladas',
            header: 'Canceladas',
            align: 'right',
            render: (l) => formatMoney(l.cancelled_guarantees.amount),
          },
          {
            key: 'propostas',
            header: 'Propostas em andamento',
            align: 'right',
            render: (l) => formatMoney(l.proposals_in_progress.amount),
          },
          {
            key: 'aprovadas',
            header: 'Com credito aprovado',
            align: 'right',
            render: (l) => formatMoney(l.proposals_credit_approved.amount),
          },
        ]}
        items={[...exposicao.lines, exposicao.total]}
        keyOf={(l) => l.modality_group || 'nao-classificado'}
      />
      <p className="text-2xs text-ink-500">
        A garantia conta uma vez, pelo valor garantido atual: o endosso altera a
        garantia e nao soma outra, e a proposta que virou garantia sai das propostas.{' '}
        {exposicao.by_metric
          ? `Pela metrica: utilizado ${formatMoney(exposicao.by_metric.used)}, reservado ${formatMoney(exposicao.by_metric.reserved)}.`
          : 'Sem metrica definida, nao ha total pela metrica.'}
      </p>
    </Bloco>
  );
}

/** Aba calculada do tomador: as linhas por grupo, a politica e a exposicao. */
export function LimiteCalculadoDoTomador({ tomadorId }: { tomadorId: string }) {
  const consulta = useQuery({
    queryKey: ['policyholder', tomadorId, 'credit-line'],
    queryFn: ({ signal }) => creditLine.ofPolicyholder(tomadorId, signal),
  });
  if (consulta.isPending) {
    return <p className="text-2xs text-ink-500">Calculando o limite...</p>;
  }
  if (consulta.error || !consulta.data) {
    return (
      <Alert tone="warning" title="Limite calculado indisponivel">
        Nao foi possivel ler o limite calculado. O limite gravado acima continua valendo
        como registro.
      </Alert>
    );
  }
  const dados = consulta.data;
  if (dados.hidden_from_broker) {
    return <Alert tone="info">{OCULTO_AO_CORRETOR}</Alert>;
  }
  return (
    <div className="flex flex-col gap-4">
      <Bloco titulo="Limite calculado">
        <Politica dados={dados} />
        <DataTable
          columns={[
            {
              key: 'grupo',
              header: 'Grupo de modalidades',
              render: (l) => grupo(l.modality_group),
            },
            {
              key: 'aprovado',
              header: 'Aprovado',
              align: 'right',
              render: (l) => dinheiro(linhaDoGrupo(l)?.approved ?? null),
            },
            {
              key: 'utilizado',
              header: 'Utilizado',
              align: 'right',
              render: (l) => {
                const d = linhaDoGrupo(l);
                return d ? <Utilizado dimensao={d} /> : '-';
              },
            },
            {
              key: 'reservado',
              header: 'Reservado',
              align: 'right',
              render: (l) => dinheiro(linhaDoGrupo(l)?.reserved ?? null),
            },
            {
              key: 'disponivel',
              header: 'Disponivel',
              align: 'right',
              render: (l) => dinheiro(linhaDoGrupo(l)?.available ?? null),
            },
            {
              key: 'validade',
              header: 'Validade',
              render: (l) => {
                const d = linhaDoGrupo(l);
                return d ? <Validade dimensao={d} /> : '-';
              },
            },
            {
              key: 'situacao',
              header: 'Situacao',
              render: (l) => {
                const d = linhaDoGrupo(l);
                return d ? (
                  <div>
                    <Situacao dimensao={d} />
                    <Motivos dimensao={d} />
                  </div>
                ) : null;
              },
            },
          ]}
          items={dados.lines}
          keyOf={(l) => l.modality_group}
        />
        <details className="text-xs">
          <summary className="cursor-pointer text-ink-500">
            Limite global e grupo economico, por grupo de modalidades
          </summary>
          <div className="mt-2 flex flex-col gap-3">
            {dados.lines.map((l) => (
              <div key={l.modality_group}>
                <p className="text-2xs font-semibold text-ink-500">
                  {grupo(l.modality_group)}
                </p>
                <TabelaDeDimensoes
                  dimensoes={l.dimensions.filter(
                    (d) => d.dimension !== 'grupo_de_modalidade',
                  )}
                />
              </div>
            ))}
          </div>
        </details>
      </Bloco>
      {dados.exposure ? <Exposicao exposicao={dados.exposure} /> : null}
    </div>
  );
}

/** Historico de consumo e liberacao do limite (a razao). */
export function HistoricoDeConsumo({ tomadorId }: { tomadorId: string }) {
  const consulta = useQuery({
    queryKey: ['policyholder', tomadorId, 'credit-line-movements'],
    queryFn: ({ signal }) => creditLine.movements(tomadorId, signal),
  });
  const itens: CreditLineMovement[] = consulta.data?.items ?? [];
  return (
    <div className="flex flex-col gap-2">
      {consulta.data?.hidden_from_broker ? (
        <Alert tone="info">{OCULTO_AO_CORRETOR}</Alert>
      ) : null}
      <DataTable
        loading={consulta.isPending}
        columns={[
          {
            key: 'data',
            header: 'Data',
            render: (m) =>
              m.calculated ? formatDate(m.created_at) : formatDateTime(m.created_at),
          },
          {
            key: 'evento',
            header: 'Evento',
            render: (m) => (
              <span>
                {rotuloEvento[m.event] ?? m.event}
                <span className="ml-1 text-2xs text-ink-500">
                  {m.kind === 'consumo' ? 'consumo' : 'liberacao'}
                </span>
                {m.calculated ? (
                  <span className="ml-1 text-2xs text-ink-400">(calculado)</span>
                ) : null}
              </span>
            ),
          },
          {
            key: 'garantia',
            header: 'Garantia',
            render: (m) =>
              m.policy_external_number ||
              (m.policy_number ? `${m.policy_number}` : '-'),
          },
          { key: 'grupo', header: 'Grupo', render: (m) => grupo(m.modality_group) },
          {
            key: 'valor',
            header: 'Valor',
            align: 'right',
            render: (m) => formatMoney(m.amount),
          },
          {
            key: 'disponivel',
            header: 'Disponivel antes e depois',
            align: 'right',
            render: (m) =>
              m.available_before || m.available_after
                ? `${dinheiro(m.available_before)} > ${dinheiro(m.available_after)}`
                : '-',
          },
          {
            key: 'conferencia',
            header: 'Conferencia',
            render: (m) => rotuloConferencia[m.verification] ?? m.verification,
          },
          { key: 'motivo', header: 'Motivo', render: (m) => m.reason },
          { key: 'usuario', header: 'Usuario', render: (m) => m.actor_label || '-' },
        ]}
        items={itens}
        keyOf={(m) => `${m.id}-${m.event}`}
        empty={
          <EmptyState
            title="Sem consumo nem liberacao"
            description="A razao registra a emissao, o cancelamento e o endosso de valor das garantias do tomador."
          />
        }
      />
    </div>
  );
}

/** Limite calculado e exposicao do grupo economico. */
export function LimiteCalculadoDoGrupo({ grupoId }: { grupoId: string }) {
  const consulta = useQuery({
    queryKey: ['economic-group', grupoId, 'credit-line'],
    queryFn: ({ signal }) => creditLine.ofEconomicGroup(grupoId, signal),
  });
  if (consulta.isPending) {
    return <p className="text-2xs text-ink-500">Calculando o limite do grupo...</p>;
  }
  if (consulta.error || !consulta.data) {
    return (
      <Alert tone="warning" title="Limite do grupo indisponivel">
        Nao foi possivel ler o limite calculado do grupo.
      </Alert>
    );
  }
  const dados = consulta.data;
  return (
    <div className="flex flex-col gap-4">
      <Bloco titulo="Limite calculado do grupo">
        {!dados.controlled ? (
          <Alert tone="warning" title="Metrica nao definida">
            {SEM_METRICA}
          </Alert>
        ) : null}
        <TabelaDeDimensoes dimensoes={dados.dimensions} />
        <p className="text-2xs text-ink-500">
          A exposicao do grupo e a soma das empresas do grupo, cada tomador uma vez,
          pela metrica da empresa ({dados.members.length} tomador(es) no grupo).
        </p>
      </Bloco>
      <Exposicao exposicao={dados.exposure} />
    </div>
  );
}
